// RecordTypeChips — the delivery report's record-type filter: all records,
// milestones, or deliverables.
//
// The filter is a server-side one. The endpoint applies it to the item list
// before any derivation runs, so switching chips refetches rather than
// narrowing a payload already on the client — the metrics, the monthly
// buckets and the late-item table must all describe the same records, and
// only the server can guarantee that.
//
// Shares the chip register with RangeChips, which sits on the same line, and
// matches its accessibility treatment: a pressed-button group rather than a
// radiogroup, because it sits beside content rather than inside a form. The
// visible labels are full words, so they need no spoken expansion.

import { clsx } from "clsx";
import {
  CHIP_ACTIVE,
  CHIP_BASE,
  CHIP_INACTIVE,
} from "./reportChipStyles";
import {
  DELIVERY_RECORD_TYPES,
  type DeliveryRecordType,
} from "../../../types/report";

const LABEL: Record<DeliveryRecordType, string> = {
  all: "All records",
  milestone: "Milestones",
  deliverable: "Deliverables",
};

export interface RecordTypeChipsProps {
  value: DeliveryRecordType;
  /** Fires on every click, including a re-click of the current selection —
   *  the caller owns whether that is a no-op or a URL rewrite. */
  onChange: (recordType: DeliveryRecordType) => void;
}

export default function RecordTypeChips({
  value,
  onChange,
}: RecordTypeChipsProps) {
  return (
    <div
      role="group"
      aria-label="Record type"
      data-testid="report-record-type-chips"
      className="flex flex-wrap items-center gap-1.5"
    >
      {DELIVERY_RECORD_TYPES.map((recordType) => {
        const active = recordType === value;
        return (
          <button
            key={recordType}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(recordType)}
            className={clsx(CHIP_BASE, active ? CHIP_ACTIVE : CHIP_INACTIVE)}
          >
            {LABEL[recordType]}
          </button>
        );
      })}
    </div>
  );
}
